import ReactGA from 'react-ga4';

const GA4_MEASUREMENT_ID = import.meta.env?.VITE_GA4_MEASUREMENT_ID;
const isDev = Boolean(import.meta.env?.DEV);
const allowDevelopmentAnalytics = import.meta.env?.VITE_GA4_ENABLE_DEV === 'true';
const enableDebugMode = isDev && import.meta.env?.VITE_GA4_DEBUG_MODE === 'true';
const SIMPLE_TYPES = new Set(['string', 'number', 'boolean']);
const ITEM_KEYS = new Set(['item_id', 'item_name', 'price', 'quantity']);
const BLOCKED_PARAM_KEYS = new Set([
  'data_url',
  'email',
  'error_detail',
  'error_message',
  'face_landmarks',
  'file_name',
  'filename',
  'image',
  'image_data',
  'mesh',
  'name',
  'coordinates',
  'landmarks',
  'message',
  'oauth_id',
  'payload',
  'phone',
  'photo',
  'user_id',
  'user_name',
  'user_name_a',
  'user_name_b',
]);
const BLOCKED_PARAM_FRAGMENTS = [
  'access_token',
  'data_url',
  'id_token',
  'landmark',
  'local_path',
  'oauth',
  'payment_key',
  'payload',
  'refresh_token',
];

const PRIVATE_VALUE_PATTERNS = [
  /^(?:data|blob):/i,
  /^[a-z]:\\/i,
  /^(?:\.{0,2}\/|\/)(?:[^/]+\/)+[^/]*$/,
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  /^(?:\+?\d[\d\s().-]{7,}\d)$/,
];

let initialized = false;
let lastPagePath = '';
let analyticsContext = {};

function shouldSendAnalytics() {
  if (!GA4_MEASUREMENT_ID) return false;
  if (!isDev) return true;
  return allowDevelopmentAnalytics;
}


function isPrivateStringValue(value) {
  return PRIVATE_VALUE_PATTERNS.some((pattern) => pattern.test(value));
}

function isSafeParamKey(key) {
  return /^[a-z][a-z0-9_]*$/.test(key)
    && !BLOCKED_PARAM_KEYS.has(key)
    && !BLOCKED_PARAM_FRAGMENTS.some((fragment) => key.includes(fragment));
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return undefined;

  const normalized = items.slice(0, 10).map((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    return Object.entries(item).reduce((safe, [key, value]) => {
      if (!ITEM_KEYS.has(key) || !SIMPLE_TYPES.has(typeof value)) return safe;
      if (typeof value === 'string' && !value.trim()) return safe;
      safe[key] = typeof value === 'string' ? value.trim().slice(0, 100) : value;
      return safe;
    }, {});
  }).filter((item) => item && item.item_id);

  return normalized.length > 0 ? normalized : undefined;
}

function normalizeAnalyticsParamsDetailed(params = {}) {
  const blockedKeys = new Set();
  if (!params || typeof params !== 'object' || Array.isArray(params)) {
    return { safeParams: {}, blockedKeys: [] };
  }

  const safeParams = Object.entries(params).reduce((safe, [key, value]) => {
    if (key === 'items') {
      const items = normalizeItems(value);
      if (items) safe.items = items;
      return safe;
    }
    if (!isSafeParamKey(key)) {
      blockedKeys.add(key);
      return safe;
    }
    if (value === undefined || value === null) return safe;
    if (!SIMPLE_TYPES.has(typeof value)) {
      blockedKeys.add(key);
      return safe;
    }
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (!normalized) return safe;
      if (isPrivateStringValue(normalized)) {
        blockedKeys.add(key);
        return safe;
      }
      safe[key] = normalized.slice(0, 100);
      return safe;
    }
    if (typeof value === 'number' && !Number.isFinite(value)) return safe;
    safe[key] = value;
    return safe;
  }, {});
  return { safeParams, blockedKeys: [...blockedKeys].sort() };
}

export function normalizeAnalyticsParams(params = {}) {
  return normalizeAnalyticsParamsDetailed(params).safeParams;
}

export function normalizePagePath(input = '') {
  if (typeof window === 'undefined') return '/';

  try {
    const parsed = new URL(input || window.location.href, window.location.origin);
    const normalizedPath = parsed.pathname
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '/:result_id')
      .replace(/\/(?:results?|analyze)\/\d+(?=\/|$)/gi, (match) => match.replace(/\d+$/, ':result_id'))
      .replace(/\/(?:results?|orders?|payments?)\/[A-Za-z0-9_-]{12,}(?=\/|$)/gi, (match) => {
        const prefix = match.slice(0, match.lastIndexOf('/') + 1);
        return `${prefix}:identifier`;
      });
    return normalizedPath || '/';
  } catch {
    return '/';
  }
}

export function normalizeSourcePage(path = '') {
  const normalized = normalizePagePath(path);
  if (normalized === '/') return 'home';
  return normalized.replace(/^\/+|\/+$/g, '').replaceAll('/', '_').replace(/[^a-zA-Z0-9_:.-]/g, '_').slice(0, 100) || 'unknown';
}

export function setAnalyticsContext(properties = {}) {
  analyticsContext = {
    ...analyticsContext,
    ...normalizeAnalyticsParams(properties),
  };
}

export function initAnalytics() {
  if (initialized || !shouldSendAnalytics()) return;

  try {
    ReactGA.initialize(GA4_MEASUREMENT_ID, {
      gtagOptions: {
        send_page_view: false,
        ...(enableDebugMode ? { debug_mode: true } : {}),
      },
    });
    initialized = true;
    if (isDev) console.debug('[analytics:init]', { enabled: true, debug_mode: enableDebugMode });
  } catch {
    if (isDev) console.debug('[analytics:init_failed]');
  }
}

export function trackPageView(path, title = '') {
  const pagePath = normalizePagePath(path);
  if (pagePath === lastPagePath) return false;
  lastPagePath = pagePath;
  setAnalyticsContext({ page_path: pagePath });

  const normalized = normalizeAnalyticsParamsDetailed({
    ...analyticsContext,
    page_path: pagePath,
    page_title: title || (typeof document !== 'undefined' ? document.title : ''),
    page_location: typeof window !== 'undefined' ? `${window.location.origin}${pagePath}` : pagePath,
  });
  const params = normalized.safeParams;

  try {
    if (isDev) console.debug('[analytics:page_view]', params, { blocked_keys: normalized.blockedKeys });
    if (!shouldSendAnalytics()) return false;
    initAnalytics();
    if (!initialized) return false;
    ReactGA.gtag('set', { page_location: params.page_location, page_path: pagePath });
    ReactGA.event('page_view', params);
    return true;
  } catch {
    if (isDev) console.debug('[analytics:page_view_failed]');
    return false;
  }
}

export function trackEvent(eventName, params = {}) {
  if (!eventName || !/^[a-z][a-z0-9_]*$/.test(eventName)) return false;
  const normalized = normalizeAnalyticsParamsDetailed({ ...analyticsContext, ...params });
  const safeParams = normalized.safeParams;
  if (enableDebugMode) safeParams.debug_mode = true;

  try {
    if (isDev) console.debug('[analytics:event]', eventName, safeParams, { blocked_keys: normalized.blockedKeys });
    if (!shouldSendAnalytics()) return false;
    initAnalytics();
    if (!initialized) return false;
    ReactGA.event(eventName, safeParams);
    return true;
  } catch {
    if (isDev) console.debug('[analytics:event_failed]', eventName);
    return false;
  }
}

function sessionEventStorageKey(eventName, dedupeKey) {
  return `startus:analytics:event:${eventName}:${String(dedupeKey).slice(0, 160)}`;
}

export function trackEventOnce(eventName, dedupeKey, params = {}) {
  if (!dedupeKey) return trackEvent(eventName, params);
  const storageKey = sessionEventStorageKey(eventName, dedupeKey);
  try {
    if (typeof window !== 'undefined' && window.sessionStorage.getItem(storageKey)) return false;
  } catch {
    // Component guards still protect the current mount when storage is unavailable.
  }
  const sent = trackEvent(eventName, params);
  if (sent || !shouldSendAnalytics()) {
    try {
      if (typeof window !== 'undefined') window.sessionStorage.setItem(storageKey, String(Date.now()));
    } catch {
      // Analytics must never interrupt the service when storage is unavailable.
    }
  }
  return sent;
}

export function setAnalyticsUserProperties(properties = {}) {
  const safeProperties = normalizeAnalyticsParams(properties);
  try {
    if (isDev) console.debug('[analytics:user_properties]', safeProperties);
    if (!shouldSendAnalytics() || Object.keys(safeProperties).length === 0) return false;
    initAnalytics();
    if (!initialized) return false;
    ReactGA.gtag('set', 'user_properties', safeProperties);
    return true;
  } catch {
    if (isDev) console.debug('[analytics:user_properties_failed]');
    return false;
  }
}

export function getDurationBucket(durationMs) {
  if (!Number.isFinite(durationMs) || durationMs < 0) return 'unknown';
  if (durationMs < 3000) return 'under_3s';
  if (durationMs < 7000) return '3_7s';
  if (durationMs < 15000) return '7_15s';
  return 'over_15s';
}

export function getScoreBucket(score) {
  const value = Number(score);
  if (!Number.isFinite(value)) return undefined;
  if (value < 40) return '0_39';
  if (value < 60) return '40_59';
  if (value < 80) return '60_79';
  return '80_100';
}

export function getFileType(mimeType = '') {
  const value = String(mimeType).toLowerCase();
  if (value.includes('jpeg') || value.includes('jpg')) return 'jpeg';
  if (value.includes('png')) return 'png';
  if (value.includes('webp')) return 'webp';
  return 'other';
}

export function getFileSizeBucket(sizeBytes) {
  const size = Number(sizeBytes) || 0;
  if (size < 1024 * 1024) return 'under_1mb';
  if (size <= 5 * 1024 * 1024) return '1_5mb';
  return 'over_5mb';
}

export function getAnalysisFailureReason(error) {
  const code = String(error?.code || '').toLowerCase();
  const status = Number(error?.status || 0);
  if (code.includes('timeout') || status === 408) return 'timeout';
  if (code.includes('credit') || code.includes('payment_required') || status === 402) return 'insufficient_credit';
  if (code.includes('invalid_response') || code.includes('parse')) return 'invalid_response';
  if (!status && (code.includes('network') || code === 'api_request_failed')) return 'network_error';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

export function getPhotoFailureReason(error) {
  const code = String(error?.code || '').toLowerCase();
  if (code.includes('format') || code.includes('decode')) return 'unsupported_format';
  if (code.includes('large')) return 'file_too_large';
  if (code.includes('multiple')) return 'multiple_faces';
  if (code.includes('face_not') || code.includes('no_face')) return 'no_face';
  if (code.includes('quality') || code.includes('confidence')) return 'low_quality';
  return 'unknown';
}

export function getFaceAnalysisErrorType(error) {
  const code = String(error?.code || '').toLowerCase();
  const status = Number(error?.status || 0);
  if (code.includes('timeout') || status === 408) return 'timeout';
  if (code.includes('face_not') || code.includes('no_face') || code.includes('invalid_image') || code.includes('unsupported_image') || code.includes('format') || code.includes('decode') || code.includes('large')) return 'invalid_image';
  if (code.includes('invalid_response') || code.includes('parse')) return 'invalid_response';
  if (!status && (code.includes('network') || code === 'api_request_failed')) return 'network_error';
  if (status >= 500) return 'server_error';
  return 'unknown';
}

export function getPaymentAnalyticsContext(productId) {
  const normalized = String(productId || '').toLowerCase();
  return normalized.startsWith('chemistry_')
    ? { feature_type: 'chemistry', product_type: 'chemistry_pass' }
    : {};
}

function purchaseStorageKey(transactionId) {
  return `startus:analytics:purchase:${transactionId}`;
}

export function trackConfirmedPurchase(confirmation, params = {}) {
  if (confirmation?.status !== 'confirmed') return false;
  return trackPurchase({ transactionId: confirmation.order_id, ...params });
}

export function trackPurchase({ transactionId, ...params }) {
  const safeTransactionId = String(transactionId || '').trim();
  if (!safeTransactionId || typeof window === 'undefined') return false;
  const key = purchaseStorageKey(safeTransactionId);

  try {
    if (window.localStorage.getItem(key)) return false;
  } catch {
    // GA4 also deduplicates purchases by transaction_id.
  }

  const sent = trackEvent('purchase', { ...params, transaction_id: safeTransactionId });
  if (sent || !shouldSendAnalytics()) {
    try {
      window.localStorage.setItem(key, String(Date.now()));
    } catch {
      // Storage can be unavailable in private browsing; transaction_id still reaches GA4.
    }
  }
  return sent;
}
